'use client';

import React, { useState } from 'react';
import clsx from 'clsx';
import Button, { ButtonType } from '../Button/Button';
import { mediaPath } from '@/mediaPath';
import styles from './CTA.module.scss';
import Accordion from '../Accordion/Accordion';

export interface CTAProps {
  className?: string;
  data: {
    title: string;
    description: string;
    darkBackground: boolean;
    buttons: {
      id: number;
      link: string;
      title: string;
      type: ButtonType;
      iconName?: string;
      target: string;
    }[],
    background: {
      url: string;
    },
    cards?: {
      id: number;
      title: string;
      description: string;
    }[]
  }
}

const CTA: React.FC<CTAProps> = ({
  className = '',
  data,
}) => {
  const isBlue = data.darkBackground;

  const ctaClasses = clsx(
    styles.cta,
    className,
    {
      [styles.isBlue]: isBlue,
    },
  );
  const [openStates, setOpenStates] = useState<boolean[]>(
    Array(data.cards?.length ?? 0).fill(true),
  );

  const toggleAccordion = (index: number) => {
    setOpenStates((prev) => prev.map((open, i) => (i === index ? !open : open)));
  };

  const isGuiding = Array.isArray(data?.cards) && data.cards.length > 0;
  const backgroundUrl = data.background?.url ? `url(${mediaPath(data.background.url)})` : '';

  const background = isBlue
    ? `
      linear-gradient(0deg, rgba(0, 44, 79, 0.80) 0%, rgba(0, 44, 79, 0.80) 100%),
      ${backgroundUrl} lightgray 50% / cover no-repeat
    `
    : `
      linear-gradient(0deg, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0.45) 100%),
      linear-gradient(180deg, #CFB1A2 0.04%, rgba(207, 177, 162, 0.50) 99.96%),
      ${backgroundUrl} lightgray 50% / cover no-repeat
    `;

  return (
    <div className={ctaClasses}>
      <div
        className={styles.backgroundImage}
        style={{ background }}
      >
        <div className={styles.content}>
          <h2 className={styles.heading}>{data.title}</h2>
          <p className={styles.text}>{data.description}</p>
          {data.buttons.length > 0 && (
          <div className={styles.buttonContainer}>
            {data.buttons.map((btn) => (
              <Button
                key={btn.id}
                type={btn.type}
                fitContent
                iconName={btn.iconName}
                className={styles.button}
                href={btn.link}
                target={btn.target}
              >
                {btn.title}
              </Button>
            ))}
          </div>
          )}
        </div>
        {isGuiding && (
        <div className={styles.guiding}>
          {[...data?.cards || []].map((card, index) => (
            <Accordion
              key={card.id}
              className={styles.item}
              title={card.title}
              description={card.description}
              toggleAccordion={() => toggleAccordion(index)}
              isOpen={openStates[index]}
              isTransparent
              iconStyle={{ fontSize: 32 }}
            />
          ))}
        </div>
        )}
      </div>
    </div>
  );
};

export default CTA;
